import { Component, OnInit, ViewChild } from '@angular/core';
import { TdDataTableService, TdDataTableSortingOrder, ITdDataTableSortChangeEvent, ITdDataTableColumn, TdDialogService, TdLoadingService, TdSearchBoxComponent } from '@covalent/core';
import { IPageChangeEvent } from '@covalent/core';
import { ViewContainerRef, OnDestroy } from '@angular/core';
import { Po } from '../po';
import { PoService } from '../po.service';
import { Subscription } from 'rxjs/Subscription';
import { Branch } from '../../../accounting/branch';
import { Observable } from 'rxjs/Observable';
import { SettingsService } from '../../../../shared/services/settings.service';
import { ExportService } from '../../../../shared/services/export.service';
import { AuthenticationService } from '../../../../shared/services/authentication.service';
import { ReceivingService } from '../../../receiving/receiving.service';
import { SalesService } from '../../../sales/sales.service';
import { ReportService } from '../../../../shared/services/report.service';
import { DateRangeDialogComponent } from '../../../../shared/dialogs/date-range-dialog/date-range-dialog.component';
import {Utils} from '../../../../shared/utils';

@Component({
  selector: 'app-po-list',
  templateUrl: './po-list.component.html',
  styleUrls: ['./po-list.component.scss'],
  providers: [ReportService]
})
export class PoListComponent implements OnInit, OnDestroy {
  @ViewChild('searchBox') _searchBox: TdSearchBoxComponent;

  data: Po[];
  subscription: Subscription = new Subscription();
  allBranches$: Observable<Branch[]>;

  branchFilter: string = 'ALL';

  columns: ITdDataTableColumn[] = [
    { name: 'po_number', label: 'PO#', tooltip: 'PO Number' },
    { name: 'supplier', label: 'Supplier' },
    { name: 'order_date', label: 'Order Date' },
    { name: 'pieces', label: 'Pieces' },
    { name: 'quantity', label: 'QTY' },
    { name: 'status', label: 'Status' },
    { name: 'branch', label: 'Branch' }
  ];

  filteredData: any[] = [];
  filteredTotal: number;
  //searchTerm: string;
  fromRow: number = 1;
  currentPage: number = 1;
  //pageSize: number = 5;
  sortBy: string = 'id';
  selectedRows: any[] = [];
  sortOrder: TdDataTableSortingOrder = TdDataTableSortingOrder.Descending;
  selectable: boolean = true;
  multipleSelect: boolean = true;

  constructor(private _dataTableService: TdDataTableService,
    private _dialogService: TdDialogService,
    private _loadingService: TdLoadingService,
    private _poService: PoService,
    private _receivingService: ReceivingService,
    private _salesService: SalesService,
    private _settingsService: SettingsService,
    private _viewContainerRef: ViewContainerRef,
    public _exportService: ExportService,
    private _reportService: ReportService,
    public _authService: AuthenticationService
  ) { }

  ngOnInit(): void {
    this._loadingService.register('loading');

    this.presetSearchBox();

    this.subscription = this._poService
      .getAllPos()
      .subscribe((pos: Po[]) => {
        // Take only the POs within my branches
        this.data = pos.filter((po: Po) => { return this._authService.getUserBranches().some((b: Branch) => { return b.code == po['branch'] }) });
        this.filteredData = this.data;
        this.filteredTotal = this.data.length;
        //this.selectedRows = this.filteredData;
        this._loadingService.resolve('loading');
        this.filter();
      });

    //Use user's branches instead
    //this.allBranches$ = this._poService.getBranches();
    this.allBranches$ = Observable.of(this._authService.getUserBranches());
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  presetSearchBox() {
    if (this._settingsService.getSearchTerm('po')) {
      this._searchBox.value = this._settingsService.getSearchTerm('po');
      this._searchBox.searchClicked();
    }
  }

  total(data: any[], field: string) {
    if (!data) return;

    return data.reduce(function (a, b) { return Number.parseFloat(a) + Number.parseFloat(b[field]) }, 0);
  }

  sort(sortEvent: ITdDataTableSortChangeEvent): void {
    this.sortOrder = sortEvent.order === TdDataTableSortingOrder.Ascending
    ? TdDataTableSortingOrder.Descending
    : TdDataTableSortingOrder.Ascending;
    
    this.sortBy = sortEvent.name;

    this.filter();
  }

  search(searchTerm: string): void {
    this._settingsService.setSearchTerm('po', searchTerm);
    //this.searchTerm = searchTerm;
    this.filter();
  }

  page(pagingEvent: IPageChangeEvent): void {
    this.fromRow = pagingEvent.fromRow;
    this.currentPage = pagingEvent.page;
    this._settingsService.setPageSize(pagingEvent.pageSize);
    this.filter();
  }

  filter(): void {
    let newData: any[] = this.data;

    // Executive users only see b2b PO
    if (this._authService.isExecutive() && !this._authService.isAdmin()) {
      newData = newData.filter(d => { return !d['supplier'] });
    }

    let excludedColumns: string[] = this.columns
      .filter((column: ITdDataTableColumn) => {
        return ((column.filter === undefined && column.hidden === true) ||
          (column.filter !== undefined && column.filter === false));
      }).map((column: ITdDataTableColumn) => {
        return column.name;
      });
    // Need to filter by branch?
    if (this.branchFilter != 'ALL') {
      newData = newData.filter((d) => { return d['branch'] == this.branchFilter });
    }

    newData = this._dataTableService.filterData(newData, this._settingsService.getSearchTerm('po'), true, excludedColumns);
    this.filteredTotal = newData.length;

    // Sort by date works differently
    if (this.sortBy == 'order_date') {
      newData = newData.sort(
        (d1, d2) => {
          return (new Date(d2[this.sortBy]).getTime() - new Date(d1[this.sortBy]).getTime()) * (this.sortOrder == TdDataTableSortingOrder.Ascending ? 1 : -1)
        }
      );
    } else {
      newData = Utils.sortData(newData, this.sortBy, this.sortOrder);
    }

    if (this._settingsService.getPageSize() + '' != 'All') {
      newData = this._dataTableService.pageData(newData, this.fromRow, this.currentPage * this._settingsService.getPageSize());
    }
    this.filteredData = newData;
  }

  openConfirm(): void {
    this._dialogService.openConfirm({
      message: 'Are you sure that you want to delete these items?',
      disableClose: false,
      title: 'Confirm',
    }).afterClosed().subscribe((accept: boolean) => {
      console.log(this.selectedRows);
      if (accept) {
        this.selectedRows.forEach(
          (row: Po) => {
            // Check whether it is external PO or B2B
            if (row['supplier']) {
              // External PO. If it was ORDERED (No packing  list yet), delete the receiving too
              if (row['status'] == 'ORDERED') {
                this._receivingService.deleteReceivingByPo(row['id'] + '').subscribe(
                  response => {
                    if (response['status'] == 'SUCCESS') {
                      this._dialogService.openAlert({
                        message: 'Receiving deleted!',
                        title: 'Success',
                      });

                      // Then delete this PO
                      this.deletePo(row);
                    } else {
                      this._dialogService.openAlert({
                        message: 'Could not delete Receiving Order!',
                        title: 'Warning',
                      })
                    }
                  },
                  error => {

                  }
                );
              } else if (row['status'] == 'DRAFT') {
                // Then delete this PO
                this.deletePo(row);
              } else {
                this._dialogService.openAlert({
                  message: 'Cannot delete external Purchase Orders that are not in DRAFT or ORDERED status',
                  title: 'Could not delete',
                })
              }
            } else {
              // Branch to branch PO. If it was ORDERED check whether the Sales order was approved and if so, delete the sales order too
              if (row['status'] == 'ORDERED') {
                this._salesService.deleteOneSaleByOrderNumber(row.po_number).subscribe(
                  response => {
                    if (response['status'] == 'SUCCESS') {
                      this._dialogService.openAlert({
                        message: 'Sales Order deleted!',
                        title: 'Success',
                      });

                      // Then delete this PO
                      this.deleteBranchPo(row);
                    } else {
                      this._dialogService.openAlert({
                        message: 'Could not delete Sales Order!',
                        title: 'Warning',
                      })
                    }
                  },
                  error => {

                  }
                );
              } else if (row['status'] == 'DRAFT') {
                // Then delete this Branch PO
                this.deleteBranchPo(row);
              } else {
                this._dialogService.openAlert({
                  message: 'Cannot delete Branch to branch Purchase Orders that are not in DRAFT or ORDERED status',
                  title: 'Could not delete',
                })
              }
            }
          }
        );
      }
      // ELSE DO NOTHING
    });
  }

  selectAll() {
    let selected = !this.allSelected();

    if (selected) {
      this.filteredData.forEach(
        item => {
          this.select(true, item);
        }
      );
    } else {
      this.selectedRows = [];
    }

  }

  select(selected: boolean, row) {
    console.log(selected);

    if (selected) {
      if (!this.selectedRows.some(item => { return item.id == row.id })) {
        this.selectedRows.push(row);
      }
    } else {
      this.selectedRows = this.selectedRows.filter(item => { return item.id != row.id })
    }
  }

  allSelected() {
    // Are there as many selected as there are in the list?
    return this.filteredData.length == this.selectedRows.length;
  }

  rowSelected(row) {
    return this.selectedRows.some((selected) => { return selected.id == row.id });
  }

  orderSchedule() {
    this._dialogService.open(DateRangeDialogComponent).afterClosed().subscribe(
      result => {
        if (result) {
          this._reportService.getOrderScheduleData(result.from, result.to).subscribe(
            orderScheduleData => {
              if (orderScheduleData.length > 0) {
                this._reportService.orderSchedule(orderScheduleData, result.from, result.to);
              } else {
                // no data
                this._dialogService.openAlert({
                  message: 'No Data for Order Schedule Report',
                  title: 'Failed!',
                })
              }
            }
          )
        }
      }
    )
  }

  productOrdersReport() {
    this._dialogService.open(DateRangeDialogComponent).afterClosed().subscribe(
      result => {
        if (result) {
          this._reportService.getProductOrdersReportData(result.from, result.to).subscribe(
            productOrdersData => {
              console.log(productOrdersData)
              this._reportService.productOrdersReport(productOrdersData, result.from, result.to);
            }
          )
        }
      }
    )
  }

  stockProjectionReport() {
    let _dialog = this._dialogService.open(DateRangeDialogComponent);
    let _date = new Date();
    _date.setMonth(_date.getMonth() - 6);

    _dialog.componentInstance.from = _date.toISOString().substr(0,10);

    _dialog.afterClosed().subscribe(
      result => {
        if (result) {
          this._reportService.getStockProjectionReport(result.from, result.to).subscribe(
            projectionData => {
              this._reportService.stockProjectionReport(projectionData, result.from, result.to);
            }
          )
        }
      }
    )
  }

  deletePo(row: any) {
    this._poService.deletePo(row['id'] + '').subscribe(
      response => {
        if (response['status'] == 'SUCCESS') {
          console.log(response);
          this._dialogService.openAlert({
            message: 'Purchase Order deleted!',
            title: 'Success',
          });

          this.selectedRows = this.selectedRows.filter(r => { return r.id != response.id });
          this.data = this.data.filter(r => { return r.id != response.id });

          this.filter();
        } else {
          this._dialogService.openAlert({
            message: 'Could not delete Purchase Order!',
            title: 'Failed!',
          })
        }
      },
      error => {

      }
    );
  }

  deleteBranchPo(row: any) {
    this._poService.deleteBranchPo(row['id'] + '').subscribe(
      response => {
        if (response['status'] == 'SUCCESS') {
          console.log(response);
          this._dialogService.openAlert({
            message: 'Purchase Order deleted!',
            title: 'Success',
          });

          this.selectedRows = this.selectedRows.filter(r => { return r.id != response.id });
          this.data = this.data.filter(r => { return r.id != response.id });

          this.filter();
        } else {
          this._dialogService.openAlert({
            message: 'Could not delete Purchase Order!',
            title: 'Failed!',
          })
        }
      },
      error => {

      }
    );
  }
}



// WEBPACK FOOTER //
// C:/Users/orion/Projects/simplicityEngine/valmust/valmustfe/src/app/agatextscreens/purchasing/pos/po-list/po-list.component.ts